import { ResponseTimeOperator } from "@redotech/redo-flows-common/rules-v3/schemas";
import {
  MESSAGE_CHANNELS_V3,
  TimeUnit,
} from "@redotech/redo-flows-common/rules-v3/types";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-flows-common/rules/types";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { WaitTimeUnit } from "@redotech/redo-flows-common/steps";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { EMAIL_CHANNELS } from "../../models/shopper-ai/conversation/channel-utils";
import { DefaultRule } from "../types";

// All non-email channels — the complement of EMAIL_CHANNELS.
// Used in both the ticket-channel condition (IS_NOT) and the
// stop-topic-continuation action (channels) so they stay in sync.
const NON_EMAIL_CHANNELS = MESSAGE_CHANNELS_V3.filter(
  (c) => !(EMAIL_CHANNELS as readonly string[]).includes(c),
);

/**
 * Default rule: Stop ticket continuation on conversation closed
 *
 * When a non-email conversation transitions to CLOSED, waits 5 days and then,
 * if the ticket has been closed for more than 5 days, archives all
 * continuation-enabled lanes so future inbound messages create a new ticket
 * rather than routing to the closed one.
 *
 * Email channels (gmail, outlook, customEmail) are intentionally excluded:
 * email reply-thread routing should continue to work regardless of ticket
 * status.
 *
 * Enabled by default.
 */
export const stopTopicContinuationOnConversationClosedRule = {
  id: "default-stop-topic-continuation-on-conversation-closed",
  name: "Stop ticket continuation on conversation closed",
  description:
    "When a non-email conversation is closed, wait 5 days, then stop routing new messages to this ticket.",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_STATUS_CHANGED,

  defaultConfig: {
    defaultRuleId: "default-stop-topic-continuation-on-conversation-closed",
    enabled: true,
    sortIndex: 10,
    configurations: {},
  },

  template: {
    name: "Stop ticket continuation on conversation closed",
    description:
      "When a non-email conversation is closed, wait 5 days, then stop routing new messages to this ticket.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_STATUS_CHANGED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: ["CLOSED"],
              queryOperator: QueryOperator.IS,
            },
            {
              conditionType: RuleConditionType.TICKET_CHANNEL,
              channels: [...EMAIL_CHANNELS],
              queryOperator: QueryOperator.IS_NOT,
            },
          ],
        },
        nextTrueId: "wait",
      },
      {
        type: StepType.WAIT,
        id: "wait",
        numDays: 5,
        timeUnit: WaitTimeUnit.DAYS,
        nextId: "conditions-after-wait",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions-after-wait",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: ["CLOSED"],
              queryOperator: QueryOperator.HAS_BEEN,
              statusTimeOperator: ResponseTimeOperator.MORE_THAN,
              value: 5,
              unit: TimeUnit.DAYS,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          {
            actionType: RuleActionType.STOP_TOPIC_CONTINUATION,
            channels: NON_EMAIL_CHANNELS,
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.FAIL_FAST,
      },
    ],
  },
} as const satisfies DefaultRule;
